import { openItemPopup } from "../shops/itemPopup";
import { PackRow, packCardHtml } from "../shops/itemCard";

/**
 * The Store's pack popup (owner 2026-10-24: "tapping most items in the shop don't open a pop up"): a tap on a pack's
 * card — anywhere but its buttons and item wells, which keep their own taps — opens the item popup with everything the
 * pack holds, a row each, its value, VIP XP and what is left, over the price and BUY. The popup reads the card as drawn
 * (dealsPages.ts / cardsPage.ts), so it always says what the card says. The card's own price button still buys at
 * once: the App Store's payment sheet is that purchase's confirm (and the web's test store adds its own).
 */

export interface PackPopupHooks {
  /** BUY in the popup: the purchase without the test store's extra confirm (the popup was it). */
  buy(offerId: string, btn: HTMLButtonElement): void;
  buyGems(packId: string, btn: HTMLButtonElement): void;
  /** The pack's name (the app's offer view). */
  name(offerId: string): string;
  /** The web's test store: the popup says nothing is charged. */
  readonly sandbox: boolean;
}

/** The test store's line in a popup. */
const SANDBOX_LINE = "Test build: nothing is charged.";
/** A card's daily item, in its row. */
const DAILY_PREFIX = "Every day · ";

const text = (el: Element | null | undefined): string => (el?.textContent ?? "").replace(/\s+/g, " ").trim();

/** A well as drawn in a card, as a plain span (no nested button in the popup). */
function spanWell(w: Element): string {
  return w.outerHTML.replace(/^<button\b/, "<span").replace(/<\/button>$/, "</span>");
}

/** What a card holds: the headline gems or builder, then every item well with its name and what it is. */
export function cardRows(card: Element): PackRow[] {
  const rows: PackRow[] = [];
  for (const h of card.querySelectorAll(".hgems, .tgems")) {
    const img = h.querySelector("img");
    rows.push({ well: `<span class="k-well sm">${img ? img.outerHTML : ""}</span>`, name: [text(h.querySelector("b")), text(h.querySelector("small"))].filter(Boolean).join(" "), detail: h.classList.contains("hbuilder") ? text(card.querySelector(".hbline")) : "" });
  }
  for (const w of card.querySelectorAll<HTMLElement>(".dwell, .k-well:not(.dwell)")) {
    if (w.closest(".tstop, .prize:not(.on), .hgems, .tgems")) continue;   // the track's rewards, the prizes not chosen
    const daily = w.closest(".cpart.grow") ? DAILY_PREFIX : "";   // a card's daily pay, after what it pays now
    rows.push({ well: spanWell(w), name: w.dataset.name ?? text(w.closest(".k-tile")?.querySelector(".k-name")), detail: `${daily}${w.dataset.detail ?? ""}` });
  }
  return rows;
}

/** The lines under the rows: the honest value, VIP XP, what is left, the test store's note. */
function cardLines(card: Element, sandbox: boolean): string[] {
  const burst = card.querySelector(".k-burst:not(.off) b"), worth = card.querySelector(".dworth");
  const xp = card.querySelector(".k-bubble")?.getAttribute("aria-label") ?? "";
  return [burst ? `<b>${text(burst)}</b> value` : worth ? text(worth) : "", xp, text(card.querySelector(".rem")), sandbox ? SANDBOX_LINE : ""];
}

/** Binds every pack card and gem pack under `root` to its popup. */
export function bindPackPopups(root: HTMLElement, parent: HTMLElement, h: PackPopupHooks): void {
  for (const card of root.querySelectorAll<HTMLElement>(".doffer[data-offer]:not(.fowned):not(.dcard.on)")) {
    card.onclick = (e) => {
      if ((e.target as Element | null)?.closest("button, a")) return;   // its price, wells, picks and prizes keep their own taps
      const id = card.dataset.offer!, btn = card.querySelector<HTMLButtonElement>(`.dbuy[data-offer="${id}"]`);
      if (!btn) return;
      const choose = btn.classList.contains("choose"), stamp = btn.classList.contains("k-stamp") ? text(btn) : "";
      void openItemPopup(parent, {
        title: h.name(id).toUpperCase(), body: packCardHtml(cardRows(card), cardLines(card, h.sandbox)), price: choose ? "" : text(btn.querySelector(".k-amt")),
        confirm: choose ? text(btn) : "BUY", blocked: stamp, className: "spack",
      }).then((ok) => { if (ok) h.buy(id, btn); });
    };
  }
  for (const tile of root.querySelectorAll<HTMLElement>(".dgem[data-pack]")) {
    tile.onclick = (e) => {
      if ((e.target as Element | null)?.closest("button")) return;
      const id = tile.dataset.pack!, btn = tile.querySelector<HTMLButtonElement>(".gbuy");
      if (!btn) return;
      const first = tile.classList.contains("first") ? "First buy: double gems" : "";
      void openItemPopup(parent, {
        title: text(tile.querySelector(".k-name")).toUpperCase(), price: text(btn.querySelector(".k-amt")), className: "spack",
        body: packCardHtml([{ well: tile.querySelector(".k-well")?.outerHTML ?? "", name: `${text(tile.querySelector(".k-count"))} gems`, detail: first }], cardLines(tile, h.sandbox)),
      }).then((ok) => { if (ok) h.buyGems(id, btn); });
    };
  }
}
